import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Phone } from 'lucide-react';

import { DocumentSign } from '@/assets/icons/DocumentSign';
import { MedicalBriefcase } from '@/assets/icons/MedicalBriefcase';
import { Stopwatch } from '@/assets/icons/Stopwatch';
import StepperCard from '@/components/common/StepperCard.tsx';
import { NavigationBar } from '@/components/NavigationBar';
import { Button } from '@/components/ui/button';
import { CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.tsx';
import {
  NEW_PATIENT_VISIT_DURATION_IN_MINUTES,
  SUPPORT_PHONE_DISPLAY,
  SUPPORT_PHONE_E164,
} from '@/lib/constants';
import { LOGGER_ACTIONS, logger } from '@/lib/logger';

const existingRxAnalyticsProperties = {
  screen: 'appointment_intro',
  source: 'telehealth_explainer',
  phone_display: SUPPORT_PHONE_DISPLAY,
};

export const TelehealthExplainer = () => {
  const navigate = useNavigate();
  const [isExistingRxDialogOpen, setIsExistingRxDialogOpen] = useState(false);
  const handleOnSubmit = () => navigate({ to: '/appointment/book-call' });
  const handleExistingRxClick = () => {
    logger.action(LOGGER_ACTIONS.INTAKE.EXISTING_RX_CLICKED, existingRxAnalyticsProperties);
    setIsExistingRxDialogOpen(true);
  };
  const handleCallClick = () => {
    logger.action(LOGGER_ACTIONS.INTAKE.EXISTING_RX_CALL_CLICKED, existingRxAnalyticsProperties);
  };

  return (
    <section>
      <NavigationBar
        onBack={() => navigate({ to: '/products/summary' })}
        backLabel="Review order"
      />
      <StepperCard currentStep={4} totalSteps={4}>
        <CardHeader>
          <CardTitle>Last step, book a quick phone visit</CardTitle>
        </CardHeader>

        <CardContent className="flex flex-col gap-6">
          <CardDescription>
            <p className="mb-1 font-medium text-orchid-600">Why is this needed?</p>
            <div>
              <p>Medicare and Medicaid require a prescription for medical equipment.</p>
              <br />
              <p>
                We partner with licensed Telehealth clinicians to make this process quick and easy.
              </p>
            </div>
          </CardDescription>

          <ul className="flex list-none flex-col gap-2">
            <FeatureItem
              icon={<Stopwatch />}
              text={`Quick ${NEW_PATIENT_VISIT_DURATION_IN_MINUTES}-minute call`}
            />
            <FeatureItem icon={<MedicalBriefcase />} text="Talk to our licensed clinicians" />
            <FeatureItem
              icon={<DocumentSign />}
              text="We handle the prescription"
              hasBorder={false}
            />
          </ul>

          <Button onClick={handleOnSubmit} className="w-full">
            Schedule appointment
          </Button>

          <Button
            type="button"
            variant="link"
            size="link"
            className="self-center text-sm"
            onClick={handleExistingRxClick}
          >
            I already have a prescription
          </Button>
        </CardContent>
      </StepperCard>

      <Dialog open={isExistingRxDialogOpen} onOpenChange={setIsExistingRxDialogOpen}>
        <DialogContent className="w-[calc(100%-32px)] max-w-[362px] gap-6 p-5 pb-8">
          <DialogHeader className="gap-6 pr-6 text-left">
            <DialogTitle>Call us with your prescription</DialogTitle>
            <DialogDescription className="font-circular text-base leading-6 text-foreground">
              Skip the telehealth visit. Call us directly and a team member will collect your
              prescription details over the phone.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-md border border-marigold-700 bg-marigold-100 px-4 py-3">
            <p className="mb-2 text-xs leading-none font-bold text-charcoal-400">BEFORE YOU CALL</p>
            <p className="text-sm leading-snug font-medium text-charcoal-900">
              Have your prescription and insurance card ready.
            </p>
          </div>

          <Button asChild className="w-full" onClick={handleCallClick}>
            <a href={`tel:${SUPPORT_PHONE_E164}`}>
              <Phone className="size-5" aria-hidden="true" />
              Call {SUPPORT_PHONE_DISPLAY}
            </a>
          </Button>

          <p className="text-center text-sm font-medium text-charcoal-400">Mon-Fri, 9am-6pm ET</p>
        </DialogContent>
      </Dialog>
    </section>
  );
};

function FeatureItem({
  icon,
  text,
  hasBorder = true,
}: {
  icon: React.ReactNode;
  text: string;
  hasBorder?: boolean;
}) {
  return (
    <li
      className={`flex items-center gap-3 p-3 ${hasBorder ? 'border-b border-hydrangea-100' : ''}`}
    >
      <div className="flex size-8 shrink-0 items-center justify-center" aria-hidden="true">
        {icon}
      </div>
      <p className="font-medium">{text}</p>
    </li>
  );
}
